import hash from 'hash-it';
import type { DefaultOptions, NormalizedCacheObject } from '@apollo/client';
import { ApolloClient, ApolloLink } from '@apollo/client';
import authTokenUtil from '../../account/authTokenUtil';
import { isFeatureEnabled } from '../../utils/features';
import { isPublicViewPath } from '../../utils/useIsPublicView';
import cache, { tryRestore } from './cache';
import getTransportLink from './getTransportLink';
import parseJsonLink from './parseJsonLink';

let singleton: {
  client: ApolloClient<NormalizedCacheObject>;
  precedingLinks: ApolloLink[];
} | null = null;
const getApolloClient = () => singleton?.client;

export function updateApolloLink(
  options: Parameters<typeof getTransportLink>[0],
) {
  singleton?.client.setLink(
    ApolloLink.from([...singleton.precedingLinks, getTransportLink(options)]),
  );
}

// Simple generational cache
let querySetOld = new Set<number>();
let querySetNew = new Set<number>();
const CACHE_TIMEOUT_MIN = isFeatureEnabled('devtools') ? 1 : 10;
setInterval(
  () => {
    querySetOld = querySetNew;
    querySetNew = new Set<number>();
  },
  1000 * 60 * CACHE_TIMEOUT_MIN,
);

const apolloClientDefaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy(
      lastFetchPolicy,
      { initialFetchPolicy, reason, options: { query, variables } },
    ) {
      const key = hash({ query, variables });
      const didHaveKey = querySetOld.has(key) || querySetNew.has(key);
      querySetNew.add(key);
      // Apollo by default resets the fetch policy to the initial fetch policy when variables
      // change. We have no reason to deviate so preserve this behavior here in most cases.
      if (reason === 'variables-changed' && !didHaveKey) {
        return initialFetchPolicy;
      }
      // After Apollo 3.1, cache value changes can lead to requerying which means `network-only`
      // or `cache-and-network` can make surprising network requests from unrelated components,
      // even leading to infinite loops. We set to `cache-first` to prevent this.
      if (
        lastFetchPolicy === 'cache-and-network' ||
        lastFetchPolicy === 'network-only'
      ) {
        return 'cache-first';
      }
      return lastFetchPolicy;
    },
  },
};

interface CreateApolloClientOptions {
  errorLink?: ApolloLink;
}
export async function createApolloClient({
  errorLink,
}: CreateApolloClientOptions = {}) {
  const isPublicView = isPublicViewPath(window.location.pathname);
  if (!isPublicView && authTokenUtil.get() != null) {
    await tryRestore();
  }

  const client = new ApolloClient({
    cache,
    assumeImmutableResults: true,
    connectToDevTools: process.env.NODE_ENV !== 'production',
    defaultOptions: apolloClientDefaultOptions,
  });

  const precedingLinks: ApolloLink[] = [parseJsonLink];
  if (errorLink) {
    precedingLinks.unshift(errorLink);
  }
  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.NODE_ENV !== 'test'
  ) {
    precedingLinks.unshift((await import('apollo-link-logger')).default);
  }

  singleton = { client, precedingLinks };

  updateApolloLink({ authToken: null, socket: null });
  return client;
}
export default getApolloClient;
